// core/guards/onboarding.guard.ts
import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { map, switchMap, catchError, take, retry, delay } from 'rxjs/operators';
import { of, from, Observable, throwError } from 'rxjs';
import { devError, devWarn, devLog } from '@core';

/**
 * OnboardingGuard - Route-aware guard for onboarding flow
 *
 * Bidirectional protection:
 * - Completed users on /onboarding/* routes → redirect to /tabs/check-ins
 * - Incomplete users on /tabs/* routes → redirect to /onboarding/name
 * - Errors on /tabs/* routes → redirect to /onboarding (fail closed with recovery path)
 * - All other cases → allow route activation
 *
 * Prevents circular redirects by:
 * - Checking current route before redirecting
 * - Returning UrlTree (not false) on errors to provide fallback destination
 *
 * Timing Protection:
 * - After OTP verification, Cloud Function may take 500ms-2s to create user doc
 * - Guard retries loadUser() with small delay if user data is null
 * - This handles race conditions without blocking navigation for too long
 */
export const onboardingGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.authState$.pipe(
    take(1), // Only take first emission to prevent infinite subscriptions
    switchMap((user) => {
      // If not authenticated, allow access to onboarding
      if (!user) {
        return of(true);
      }

      // Load user profile from Firestore to check onboarding status
      // Note: OnboardingService.verifyOtp() calls waitForUserCreation() which should
      // ensure the user document exists before navigating. However, as a safety net,
      // this guard will also retry if the document is not found immediately.
      return from(userService.loadUser(user.uid)).pipe(
        switchMap((userData) => {
          // If userData is null on first attempt, retry after a small delay
          // This handles edge cases where guard executes before waitForUserCreation() completes
          if (!userData) {
            devLog('[OnboardingGuard] User data not found on first attempt, retrying...');
            return throwError(() => new Error('USER_DATA_NOT_FOUND'));
          }
          return of(userData);
        }),
        retry({
          count: 3,
          delay: (error, retryCount) => {
            // Only retry for USER_DATA_NOT_FOUND errors
            if (error.message === 'USER_DATA_NOT_FOUND') {
              const delayMs = 300 * retryCount; // 300ms, 600ms, 900ms
              devLog(`[OnboardingGuard] Retrying loadUser() in ${delayMs}ms (attempt ${retryCount})`);
              return of(null).pipe(delay(delayMs));
            }
            // For other errors, don't retry
            return throwError(() => error);
          }
        }),
        map((userData) => {
          // Route-aware guard logic - only redirect when user is on WRONG route type
          let result: boolean | import('@angular/router').UrlTree;

          // FIX H-02: Handle null userData (user doc doesn't exist after retries) - fail closed for protected routes
          if (!userData && state.url.startsWith('/tabs')) {
            devWarn('[OnboardingGuard] User data not found after retries, denying access to protected route');
            result = router.createUrlTree(['/onboarding']);
          }
          // Redirect completed users AWAY from onboarding routes
          else if (userData?.onboardingComplete && state.url.startsWith('/onboarding')) {
            devLog('[OnboardingGuard] User onboarding complete, redirecting to app');
            result = router.createUrlTree(['/tabs/check-ins']);
          }
          // Redirect incomplete users AWAY from protected routes
          else if (userData && !userData.onboardingComplete && state.url.startsWith('/tabs')) {
            devLog('[OnboardingGuard] User onboarding incomplete, redirecting to name entry');
            result = router.createUrlTree(['/onboarding/name']);
          }
          // Allow route activation in all other cases
          else {
            result = true;
          }

          return result;
        }),
        catchError((error): Observable<boolean | UrlTree> => {
          devError('[OnboardingGuard] Error loading user:', error);

          // FIX C-01: Fail closed with recovery path - redirect to onboarding instead of returning false
          if (state.url.startsWith('/tabs')) {
            devWarn('[OnboardingGuard] Redirecting to onboarding due to error loading user data');
            return of(router.createUrlTree(['/onboarding'])); // UrlTree prevents redirect loop
          }

          // Allow access to onboarding routes for error recovery
          return of(true);
        })
      );
    })
  );
};
